import moment from 'moment';
import type { InternalDataSourceConfig } from '../core/schema/types';
import { DATE_FORMAT } from '@packages/shared/constants';
import type { FetchParams } from '../hooks/useRemoteDataV2';

export function resolveRequestToken(
  token: string,
  p: FetchParams,
  pageIndex: number = p.pageIndex,
  pageSize: number = p.pageSize,
): unknown {
  switch (token) {
    case 'table.searchText':
      return p.searchText || undefined;
    case 'table.pageSize':
      return pageSize;
    case 'table.pageOffset':
      return pageIndex * pageSize;
    case 'table.sorting':
      return p.sort ? `${p.sort.field} ${p.sort.direction}` : undefined;
    default: {
      if (token.startsWith('static.')) {
        const literal = token.slice('static.'.length);
        if (literal === 'undefined') return undefined;
        if (literal.startsWith('[')) {
          try {
            return JSON.parse(literal) as unknown;
          } catch {
            return undefined;
          }
        }
        const num = Number(literal);
        return Number.isNaN(num) ? literal : num;
      }
      if (token.startsWith('filters.')) {
        const filterKey = token.slice('filters.'.length);
        if (filterKey.endsWith('OrDefault')) {
          const name = filterKey.slice(0, -'OrDefault'.length);
          const value = p.filters[name];
          if (
            value === undefined ||
            value === null ||
            value === '' ||
            (Array.isArray(value) && value.length === 0)
          ) {
            return [0];
          }
          return value;
        }
        const value = p.filters[filterKey];
        if (value === undefined || value === null || value === '') return undefined;
        return value;
      }
      return undefined;
    }
  }
}

function mappedFilterFields(requestParams: Record<string, string>): Set<string> {
  const mapped = new Set<string>();
  for (const token of Object.values(requestParams)) {
    if (typeof token === 'string' && token.startsWith('filters.')) {
      mapped.add(token.slice('filters.'.length));
    }
  }
  return mapped;
}

/**
 * Substitutes `{paramName}` placeholders in a relative API path using resolved query param values.
 * Unresolved placeholders are left as-is so callers can detect missing filters.
 */
export function applyEndpointPathParams(
  endpoint: string,
  params: Record<string, unknown>,
): string {
  return endpoint.replace(/\{([^}]+)\}/g, (match, key: string) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') return match;
    return encodeURIComponent(String(value));
  });
}

/** Path template placeholders, e.g. `/items/{Id}` → `['Id']`. */
export function extractEndpointPathParamKeys(endpoint: string): string[] {
  return [...endpoint.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]);
}

/**
 * Substitutes `{param}` segments in the endpoint, backfilling from table filters when needed.
 * Drops resolved path keys from the query object so they are not duplicated (e.g. `?Id=`).
 */
export function buildResolvedRequestPath(
  endpoint: string,
  queryParams: Record<string, unknown>,
  filters: Record<string, unknown>,
): { path: string; queryParams: Record<string, unknown> } {
  const merged: Record<string, unknown> = { ...queryParams };
  for (const key of extractEndpointPathParamKeys(endpoint)) {
    if (merged[key] != null && merged[key] !== '') continue;
    const fromFilter = filters[key];
    if (fromFilter != null && fromFilter !== '') {
      merged[key] = fromFilter;
    }
  }

  const path = applyEndpointPathParams(endpoint.replace(/^\//, ''), merged);
  const nextQuery = { ...merged };
  for (const key of extractEndpointPathParamKeys(endpoint)) {
    if (!path.includes(`{${key}}`)) {
      delete nextQuery[key];
    }
  }
  return { path, queryParams: nextQuery };
}

export function buildQueryParams(
  dataSource: Readonly<InternalDataSourceConfig>,
  p: FetchParams,
  pageIndex: number = p.pageIndex,
  pageSize: number = p.pageSize,
): Record<string, unknown> {
  const params: Record<string, unknown> = { ...dataSource.staticQueryParams };

  for (const [paramKey, tokenOrLiteral] of Object.entries(dataSource.requestParams)) {
    const resolved = resolveRequestToken(tokenOrLiteral, p, pageIndex, pageSize);
    if (resolved !== undefined && resolved !== null && resolved !== '') {
      params[paramKey] = resolved;
    }
  }

  const mappedFilters = mappedFilterFields(dataSource.requestParams);
  for (const [filterName, filterValue] of Object.entries(p.filters)) {
    if (mappedFilters.has(filterName)) continue;
    if (filterValue !== undefined && filterValue !== null && filterValue !== '') {
      params[filterName] = filterValue;
    }
  }

  return params;
}

export function getNestedValue(obj: unknown, path: string): unknown {
  if (!path || !obj || typeof obj !== 'object') return obj;
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

export function formatContinuationCursorValue(
  raw: unknown,
  format: 'preserve' | 'iso' | 'mmddyyyy',
): string | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (format === 'preserve') {
    if (raw instanceof Date) return raw.toISOString();
    if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
      return String(raw);
    }
    return undefined;
  }
  const m = moment(raw as string | number | Date);
  if (!m.isValid()) {
    return typeof raw === 'string' ? raw : String(raw);
  }
  if (format === 'iso') return m.toISOString();
  return m.format(DATE_FORMAT);
}

/**
 * Serializes query params matching ABP/.NET convention:
 * arrays repeat the key (e.g. `Ids=1&Ids=2`) instead of bracket notation.
 */
export function serializeQueryParams(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null) {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
        }
      }
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.join('&');
}
